import { useState, useRef } from 'react'
import { ACTIVITIES_FALLBACK } from '../services/dates.js'

const UNITS = ['L', 'mL', 'g', 'kg', 't']

// currentProducts: { product_name, dose, unit }[]
// onSave(items): passa { product_name, dose, unit }[]
export default function ProductEditor({ area, actKey, currentProducts, onSave, onClose }) {
  const [items, setItems] = useState(currentProducts.map(p => ({
    product_name: p.product_name,
    dose: p.dose ?? '',
    unit: p.unit ?? 'L',
  })))
  const [newName, setNewName] = useState('')
  const [newDose, setNewDose] = useState('')
  const [newUnit, setNewUnit] = useState('L')
  const [saving, setSaving] = useState(false)
  const nameRef = useRef()

  function add() {
    const name = newName.trim()
    if (!name || items.some(i => i.product_name === name)) return
    setItems(prev => [...prev, {
      product_name: name,
      dose: newDose !== '' ? newDose : '',
      unit: newUnit,
    }])
    setNewName('')
    setNewDose('')
    nameRef.current?.focus()
  }

  async function handleSave() {
    setSaving(true)
    try {
      await onSave(items.map(i => ({
        product_name: i.product_name,
        dose: i.dose !== '' ? parseFloat(i.dose) : null,
        unit: i.dose !== '' ? i.unit : null,
      })))
    } finally { setSaving(false) }
  }

  const label = ACTIVITIES_FALLBACK.find(a => a.key === actKey)?.label ?? actKey

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 480, boxShadow: '0 30px 70px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
          Produtos / Insumos
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#14532d', marginBottom: 2 }}>{area.name}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>{label}</div>

        {/* Lista de produtos existentes */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 48, marginBottom: 20 }}>
          {items.length === 0 && (
            <span style={{ color: '#94a3b8', fontSize: 13 }}>Nenhum produto definido ainda</span>
          )}
          {items.map(p => (
            <span key={p.product_name} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#f0fdf4', color: '#14532d',
              border: '1.5px solid #86efac',
              borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600,
            }}>
              {p.product_name}
              {p.dose !== '' && p.dose != null && (
                <span style={{ fontWeight: 400, color: '#16a34a' }}>
                  &nbsp;{p.dose}{p.unit}/ha
                </span>
              )}
              <button
                onClick={() => setItems(prev => prev.filter(i => i.product_name !== p.product_name))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, lineHeight: 1, padding: 0 }}
              >×</button>
            </span>
          ))}
        </div>

        {/* Inputs para novo produto */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
          <input
            ref={nameRef}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="Ex: Roundup, Glifosato 480..."
            style={{
              flex: '1 1 160px', padding: '10px 14px',
              border: '2px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', fontFamily: 'inherit',
            }}
          />
          <input
            type="number"
            value={newDose}
            onChange={e => setNewDose(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="Dose"
            min="0"
            step="0.01"
            style={{
              width: 80, padding: '10px 10px',
              border: '2px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', fontFamily: 'inherit',
            }}
          />
          <select
            value={newUnit}
            onChange={e => setNewUnit(e.target.value)}
            style={{
              padding: '10px 8px', border: '2px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff',
            }}
          >
            {UNITS.map(u => <option key={u} value={u}>{u}/ha</option>)}
          </select>
          <button onClick={add} style={{
            background: '#16a34a', color: '#fff', border: 'none',
            borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 800, fontSize: 18,
          }}>+</button>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', border: '2px solid #e2e8f0', borderRadius: 10,
            background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#64748b',
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, padding: '12px', border: 'none', borderRadius: 10,
            background: '#15803d', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700,
          }}>{saving ? 'Salvando...' : 'Salvar produtos'}</button>
        </div>
      </div>
    </div>
  )
}
